import moment from 'moment';

/*
  Cycle phase rules for the coach view. Mirrors the mobile app's `utils/cycle.ts` (the client sees the same phases);
  keep the two in step. Standard values come from "Menstrual Cycle — Standard Durations & Hormonal Phases".
*/
const DATE_FORMAT = 'YYYY-MM-DD';
const STANDARD_PERIOD_LENGTH = 5;
const STANDARD_CYCLE_LENGTH = 28;
const LUTEAL_PHASE_LENGTH = 14;
// Unlogged days allowed inside one period: period days at most this many days apart belong to the same period.
const PERIOD_LOG_MAX_GAP_DAYS = 2;
const CHECK_IN_WINDOW_DAYS = 7;

const CyclePhase = {
  MENSTRUAL: 'menstrual',
  FOLLICULAR: 'follicular',
  OVULATORY: 'ovulatory',
  LUTEAL: 'luteal',
};

// The first day of each period among `periodDates`: a date with no other one shortly before it, so a skipped day
// mid-period (up to PERIOD_LOG_MAX_GAP_DAYS) does not start a new period.
const getPeriodStarts = (periodDates) => {
  const sorted = [...new Set(periodDates)].sort();
  return sorted.filter((dateId, index) => index === 0
    || moment(dateId).diff(moment(sorted[index - 1]), 'days') > PERIOD_LOG_MAX_GAP_DAYS + 1);
};

// Period starts from the logged period days together with the setup's last period start, which counts as a period
// day: logging the next day or two after it continues that period instead of re-anchoring on the logged day.
const getAllPeriodStarts = (settings, logs) => getPeriodStarts([
  ...Object.keys(logs).filter((dateId) => logs[dateId].isPeriod),
  ...(settings.lastPeriodStart ? [settings.lastPeriodStart] : []),
]);

// Whether the period that started on `startId` was marked as ended before `dateId`: the latest period day logged in
// between is marked as the last day. A period day logged after a marked last day means the period carried on.
const hasPeriodEndedBefore = (startId, dateId, logs) => {
  const latestPeriodDay = Object.keys(logs)
    .filter((logDate) => logs[logDate].isPeriod && logDate >= startId && logDate < dateId)
    .sort()
    .pop();
  return !!latestPeriodDay && !!logs[latestPeriodDay].isLastPeriodDay;
};

/**
 * Cycle day and phase for a date, or null when there is no period start to anchor on. Logged period and ovulation
 * days win; every other day is estimated from the nearest period start and the client's lengths.
 *
 * @param {string} dateId The day (YYYY-MM-DD).
 * @param {Object} settings { periodLength, cycleLength, lastPeriodStart }.
 * @param {Object} logs Day logs keyed by date id.
 * @returns {Object|null} { cycleDay, phase, isEstimated }.
 */
const getCycleDayInfo = (dateId, settings, logs) => {
  const periodLength = settings.periodLength || STANDARD_PERIOD_LENGTH;
  const cycleLength = settings.cycleLength || STANDARD_CYCLE_LENGTH;
  const anchor = getAllPeriodStarts(settings, logs)
    .filter((start) => start <= dateId)
    .pop();
  if (!anchor) return null;

  const log = logs[dateId];
  const daysSinceAnchor = moment(dateId).diff(moment(anchor), 'days');
  const cycleDay = (daysSinceAnchor % cycleLength) + 1;
  const ovulationDay = Math.max(cycleLength - LUTEAL_PHASE_LENGTH, periodLength + 1);
  // A period the client marked as ended stops on that day.
  const hasLoggedEnd = daysSinceAnchor < cycleLength && hasPeriodEndedBefore(anchor, dateId, logs);

  if (log?.isPeriod) return { cycleDay, phase: CyclePhase.MENSTRUAL, isEstimated: false };
  if (log?.ovulated) return { cycleDay, phase: CyclePhase.OVULATORY, isEstimated: false };
  if (cycleDay <= periodLength && !hasLoggedEnd) return { cycleDay, phase: CyclePhase.MENSTRUAL, isEstimated: true };
  if (cycleDay < ovulationDay) return { cycleDay, phase: CyclePhase.FOLLICULAR, isEstimated: true };
  if (cycleDay === ovulationDay) return { cycleDay, phase: CyclePhase.OVULATORY, isEstimated: true };
  return { cycleDay, phase: CyclePhase.LUTEAL, isEstimated: true };
};

/**
 * The coach's view of one week: the seven days up to and including `windowEndId`. Days are the client's local dates
 * (YYYY-MM-DD), the same ids their day logs use.
 *
 * @param {Object} settings { periodLength, cycleLength, lastPeriodStart }.
 * @param {Object} logs Day logs keyed by date id.
 * @param {string} windowEndId The last day of the week (YYYY-MM-DD).
 * @returns {Object} { days, currentPhase, hasSymptoms }. `currentPhase` is null when no phase can be worked out.
 */
const getCycleWeekSummary = (settings, logs, windowEndId) => {
  const end = moment(windowEndId, DATE_FORMAT);
  const days = Array.from({ length: CHECK_IN_WINDOW_DAYS }, (_, index) => {
    const dateId = end.clone().subtract(CHECK_IN_WINDOW_DAYS - 1 - index, 'days').format(DATE_FORMAT);
    const dayInfo = getCycleDayInfo(dateId, settings, logs);

    return {
      dateId,
      cycleDay: dayInfo?.cycleDay ?? null,
      phase: dayInfo?.phase ?? null,
      isEstimated: dayInfo?.isEstimated ?? true,
      symptoms: logs[dateId]?.symptoms ?? [],
    };
  });

  const lastDay = days[days.length - 1];
  return {
    days,
    currentPhase: lastDay.phase ? lastDay : null,
    hasSymptoms: days.some(({ symptoms }) => symptoms.length > 0),
  };
};

export {
  DATE_FORMAT,
  CyclePhase,
  getCycleDayInfo,
  getCycleWeekSummary,
};
